import React from 'react';
import { ShieldCheck, CheckCircle, AlertCircle } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';
import { useBlockEditor } from '../../hooks/useBlockEditor';
import { EditableField } from '../editing';

/**
 * Card editor para Visto Americano
 * 
 * Produto template — preço fixo R$ 1.850 (tudo incluso).
 * Variações: primeira via, renovação sem entrevista, renovação com entrevista.
 * 
 * Schema esperado (details):
 * - service, visa_type ("new" | "renewal" | "renewal_interview")
 * - interview_required: boolean
 * - includes: string[]
 * - consulate_cities: string[] (quando com entrevista)
 * - renewal_note: string (quando sem entrevista)
 */
export default function VisaCard({ block, onRemove, quoteId, onBlockUpdate }) {
  const editable = !!quoteId;
  const { saving, updateField, updateDetail, isSaved } =
    useBlockEditor(quoteId, block, onBlockUpdate);

  const details = block.details || {};
  const includes = details.includes || [];
  const interviewRequired = details.interview_required;

  const E = (value, onSave, opts = {}) => {
    if (!editable) return opts.fallback || value || opts.placeholder || '-';
    return (
      <EditableField
        value={value}
        onSave={onSave}
        type={opts.type || 'text'}
        options={opts.options}
        saving={saving}
        saved={opts.saved}
        placeholder={opts.placeholder || '-'}
        style={opts.style}
        validate={opts.validate}
      />
    );
  };

  const visaTypeLabel = () => {
    switch (details.visa_type) {
      case 'new': return 'Primeira Via';
      case 'renewal': return 'Renovação sem Entrevista';
      case 'renewal_interview': return 'Renovação com Entrevista';
      default: return 'Visto Americano';
    }
  };

  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md, padding: '1.5rem', position: 'relative',
    }}>
      <button onClick={() => onRemove(block.id)} style={{
        position: 'absolute', top: '1rem', right: '1rem',
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text.muted, fontSize: '1.25rem',
      }}>×</button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', backgroundColor: `${colors.primary}10`,
          borderRadius: borderRadius.sm, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <ShieldCheck size={24} color={colors.primary} />
        </div>

        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.primary, margin: '0 0 0.25rem 0' }}>
            {E(block.title, (v) => updateField('title', v), { saved: isSaved('title'), fallback: block.title })}
          </h4>

          {/* Badge do tipo */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.25rem 0.625rem', borderRadius: '9999px', marginBottom: '1rem',
            backgroundColor: interviewRequired ? `${colors.warm}15` : `${colors.accent}15`,
          }}>
            {interviewRequired ? (
              <AlertCircle size={12} color={colors.warm} />
            ) : (
              <CheckCircle size={12} color={colors.accent} />
            )}
            <span style={{
              fontSize: '0.6875rem', fontWeight: 600,
              color: interviewRequired ? colors.warm : colors.accent,
            }}>
              {visaTypeLabel()}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Entrevista */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Entrevista</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {interviewRequired ? 'Necessária' : 'Não necessária'}
              </p>
            </div>

            {/* Consulado (se com entrevista) */}
            {interviewRequired && details.consulate_cities && (
              <div>
                <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Consulados</p>
                <p style={{ fontSize: '0.8125rem', fontWeight: 500, color: colors.text.primary, margin: 0 }}>
                  {details.consulate_cities.join(', ')}
                </p>
              </div>
            )}

            {/* Nota renovação sem entrevista */}
            {!interviewRequired && details.renewal_note && (
              <div>
                <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Observação</p>
                <p style={{ fontSize: '0.8125rem', fontWeight: 500, color: colors.accent, margin: 0 }}>
                  {details.renewal_note}
                </p>
              </div>
            )}

            {/* Valor */}
            <div style={{ gridColumn: '1 / -1', textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Valor</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.accent, margin: 0 }}>
                {E(block.price || 0, (v) => updateField('price', v), {
                  type: 'currency', saved: isSaved('price'),
                  validate: (v) => v < 0 ? 'Preço inválido' : null,
                  fallback: `R$ ${(block.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                })}
              </p>
            </div>
          </div>

          {/* Inclusos */}
          {includes.length > 0 && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.5rem' }}>Incluso no serviço</p>
              {includes.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <CheckCircle size={14} color={colors.accent} />
                  <span style={{ fontSize: '0.8125rem', color: colors.text.secondary }}>{item}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
